/**
 * PhotoLightbox - full-size evidence photo viewer with carousel navigation
 * and (optionally) annotation pins, flags and notes.
 *
 * Extracted verbatim from photo-review-panel.tsx so the claim Evidence card
 * can reuse the viewer. Two behaviour deltas vs the original:
 *   - `annotationsEnabled` prop (default true): when false the annotation
 *     sidebar is not rendered and no annotation writes ever fire - a pure
 *     carousel for tenants without the lab flag (the annotation endpoints
 *     404 behind the flag).
 *   - `handleSave` is guarded so read-only/annotations-off viewers no longer
 *     autosave annotations on navigate/close (previously caused stray
 *     update_or_create writes and, without the lab flag, 404 error toasts).
 */

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Download,
  ExternalLink,
  Flag,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Save,
  X,
  Plus,
  Minus,
  RotateCcw,
  Maximize2,
  Minimize2,
} from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { fileDownloadUrl } from '@/shared/lib/authenticated-file'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Input } from '@/shared/components/ui/input'
import {
  useCreateEvidenceAnnotation,
  useUpdateEvidenceAnnotation,
  useCreateEvidencePin,
  useUpdateEvidencePin,
  useDeleteEvidencePin,
  type EvidenceAnnotation,
  type EvidencePin,
} from '@/admin/hooks/use-lab-tasks'
import { useEvidenceFlagCategories } from '@/admin/pages/claims/use-claim-attachments'
import { useFormatters } from '@/shared/lib/formatting'

// Single source of truth for pin colours - the photo pins AND the
// annotation list both key off pin_number so they always match.
const PIN_PALETTE = [
  { dot: 'bg-[var(--green)]', text: 'text-[var(--green)]', border: 'border-[var(--green)]', ring: 'ring-[var(--green)]/40' },
  { dot: 'bg-[var(--status-info)]', text: 'text-[var(--status-info)]', border: 'border-[var(--status-info)]', ring: 'ring-[var(--status-info)]/40' },
  { dot: 'bg-[var(--status-warn)]', text: 'text-[var(--status-warn)]', border: 'border-[var(--status-warn)]', ring: 'ring-[var(--status-warn)]/40' },
  { dot: 'bg-[var(--status-error)]', text: 'text-[var(--status-error)]', border: 'border-[var(--status-error)]', ring: 'ring-[var(--status-error)]/40' },
]

function pinPalette(pinNumber: number | null | undefined) {
  const i = (pinNumber ?? 1) - 1
  return PIN_PALETTE[
    ((i % PIN_PALETTE.length) + PIN_PALETTE.length) % PIN_PALETTE.length
  ]
}

export interface EvidenceFile {
  uuid: string
  file_url: string | null
  file_name: string
}

// ── Callout label on image (always visible) ─────────────────────────

function PinCallout({
  pin,
  isActive,
  onClick,
  claimUuid,
  annotationUuid,
  containerRef,
  readOnly,
}: {
  pin: EvidencePin
  isActive: boolean
  onClick: (e: React.MouseEvent) => void
  claimUuid: string
  annotationUuid: string
  containerRef: React.RefObject<HTMLDivElement | null>
  readOnly: boolean
}) {
  const pal = pinPalette(pin.pin_number)
  const isRS = pin.created_by_role === 'rs'
  const dotColor = pal.dot
  const labelBg = `bg-[var(--bg-light)] ${pal.border} ${pal.text}`
  const labelBgActive = `bg-[var(--bg-light)] ${pal.border} ${pal.text} ring-2 ${pal.ring}`

  const updatePin = useUpdateEvidencePin(claimUuid, annotationUuid, pin.uuid)
  const [drag, setDrag] = useState<{ x: number; y: number } | null>(null)
  const movedRef = useRef(false)
  const startRef = useRef<{ x: number; y: number } | null>(null)

  const pointerToPct = (e: React.PointerEvent) => {
    const el = containerRef.current
    if (!el) return null
    const r = el.getBoundingClientRect()
    const clamp = (v: number) => Math.max(0, Math.min(100, v))
    return {
      x: clamp(((e.clientX - r.left) / r.width) * 100),
      y: clamp(((e.clientY - r.top) / r.height) * 100),
    }
  }

  const left = drag ? drag.x : pin.x_pct
  const top = drag ? drag.y : pin.y_pct

  return (
    <div className="absolute z-10" style={{ left: `${left}%`, top: `${top}%` }}>
      {/* Pin dot - click to edit, drag to reposition if misplaced */}
      <button
        type="button"
        className={cn(
          'absolute -translate-x-1/2 -translate-y-1/2 flex h-5 w-5 touch-none select-none items-center justify-center rounded-full border-2 border-white text-[9px] font-bold text-white shadow-md transition-transform',
          dotColor,
          isActive && 'scale-125',
          !readOnly && 'cursor-grab active:cursor-grabbing'
        )}
        onPointerDown={(e) => {
          if (readOnly) return
          e.stopPropagation()
          e.currentTarget.setPointerCapture?.(e.pointerId)
          movedRef.current = false
          startRef.current = { x: e.clientX, y: e.clientY }
        }}
        onPointerMove={(e) => {
          if (readOnly || !startRef.current) return
          e.stopPropagation()
          if (
            !movedRef.current &&
            Math.hypot(
              e.clientX - startRef.current.x,
              e.clientY - startRef.current.y
            ) < 4
          )
            return
          movedRef.current = true
          const p = pointerToPct(e)
          if (p) setDrag(p)
        }}
        onPointerUp={(e) => {
          if (readOnly) return
          e.stopPropagation()
          const moved = movedRef.current
          startRef.current = null
          if (moved) {
            const p = pointerToPct(e)
            if (p) {
              updatePin.mutate(
                {
                  x_pct: Math.round(p.x * 100) / 100,
                  y_pct: Math.round(p.y * 100) / 100,
                  comment: pin.comment ?? '',
                },
                { onSettled: () => setDrag(null) }
              )
            } else {
              setDrag(null)
            }
          } else {
            setDrag(null)
          }
        }}
        onClick={(e) => {
          e.stopPropagation()
          if (movedRef.current) {
            movedRef.current = false
            return
          }
          onClick(e)
        }}
      >
        {pin.pin_number}
      </button>

      {/* Label callout - always visible */}
      {pin.comment && (
        <button
          type="button"
          onClick={onClick}
          className={cn(
            'absolute left-3 top-1 max-w-[180px] cursor-pointer rounded-[4px] border-[0.5px] px-2 py-0.5 text-left shadow-sm transition-all',
            isActive ? labelBgActive : labelBg
          )}
        >
          <span className="block truncate text-[11px] font-medium leading-tight">
            {pin.comment}
          </span>
          <span className="block text-[8px] opacity-60">
            {pin.created_by_name ?? ''}{isRS ? ' (RS)' : ' (Lab)'}
          </span>
        </button>
      )}
    </div>
  )
}

// ── Edit popover (shown when clicking a pin) ────────────────────────

function PinEditPopover({
  pin,
  onSave,
  onDelete,
  onClose,
  readOnly,
}: {
  pin: EvidencePin
  onSave: (comment: string) => void
  onDelete: () => void
  onClose: () => void
  readOnly: boolean
}) {
  const { formatDate } = useFormatters()
  const [comment, setComment] = useState(pin.comment ?? '')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!readOnly) inputRef.current?.focus()
  }, [readOnly])

  return (
    <div
      className="absolute z-30 w-[240px] rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] shadow-lg"
      style={{
        top: `${Number(pin.y_pct) + 3}%`,
        ...(Number(pin.x_pct) > 60
          ? { right: `${100 - Number(pin.x_pct) + 2}%` }
          : { left: `${Number(pin.x_pct) + 2}%` }),
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex flex-col gap-2 p-2.5">
        <p className="text-[10px] text-[var(--ink-3)]">
          {pin.created_by_name ?? 'Unknown'}
          {pin.created_by_role && ` · ${pin.created_by_role.toUpperCase()}`}
          {pin.created_at && ` · ${formatDate(pin.created_at)}`}
        </p>

        {readOnly ? (
          <p className="text-[12px] text-[var(--ink)]">{pin.comment || 'No label'}</p>
        ) : (
          <div className="flex gap-1.5">
            <Input
              ref={inputRef}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Label this area…"
              className="h-7 text-[12px]"
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSave(comment)
                if (e.key === 'Escape') onClose()
              }}
            />
            <Button
              size="icon-xs"
              onClick={() => onSave(comment)}
              className="shrink-0"
              title="Save"
            >
              <Save className="h-3 w-3" />
            </Button>
          </div>
        )}

        <div className="flex items-center justify-between">
          {!readOnly && (
            confirmDelete ? (
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-red-600">Remove pin?</span>
                <button
                  type="button"
                  onClick={onDelete}
                  className="text-[10px] font-medium text-red-600 hover:text-red-700"
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(false)}
                  className="text-[10px] text-[var(--ink-3)] hover:text-[var(--ink-2)]"
                >
                  No
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="flex items-center gap-1 text-[10px] text-red-500 hover:text-red-600"
              >
                <Trash2 className="h-2.5 w-2.5" />
                Remove
              </button>
            )
          )}
          <button
            type="button"
            onClick={onClose}
            className="ml-auto text-[10px] text-[var(--ink-3)] hover:text-[var(--ink-2)]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

// ── New pin input (inline callout being created) ────────────────────

function NewPinInput({
  position,
  onSave,
  onCancel,
}: {
  position: { x: number; y: number }
  onSave: (comment: string) => void
  onCancel: () => void
}) {
  const [comment, setComment] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  return (
    <div
      className="absolute z-30"
      style={{ left: `${position.x}%`, top: `${position.y}%` }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Placement dot */}
      <div className="absolute -translate-x-1/2 -translate-y-1/2 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-[var(--green)] text-[9px] font-bold text-white shadow-md">
        +
      </div>

      {/* Inline label input */}
      <div className="absolute left-3 top-1 flex items-center gap-1 rounded-[4px] border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)] px-1.5 py-0.5 shadow-sm">
        <Input
          ref={inputRef}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Label (optional)…"
          className="h-6 w-[130px] border-0 bg-transparent p-0 text-[11px] font-medium text-[var(--ink)] placeholder:text-[var(--ink-3)] focus-visible:ring-0"
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSave(comment.trim())
            if (e.key === 'Escape') onCancel()
          }}
        />
        <Button
          size="icon-xs"
          variant="ghost"
          onClick={() => onSave(comment.trim())}
          className="h-4 w-4 shrink-0 text-[var(--green)] hover:bg-[var(--green-bg)] hover:text-[var(--green-hover)]"
          title="Save pin"
        >
          <Save className="h-2.5 w-2.5" />
        </Button>
        <Button
          size="icon-xs"
          variant="ghost"
          onClick={onCancel}
          className="h-4 w-4 shrink-0 text-[var(--ink-3)] hover:bg-[var(--bg-subtle)] hover:text-[var(--ink)]"
          title="Discard pin"
        >
          <X className="h-2.5 w-2.5" />
        </Button>
      </div>
    </div>
  )
}

// ── Lightbox ────────────────────────────────────────────────────────

export function PhotoLightbox({
  files,
  annotations,
  currentIndex,
  onIndexChange,
  onClose,
  claimUuid,
  taskUuid,
  readOnly,
  userRole,
  annotationsEnabled = true,
  captionFor,
}: {
  files: EvidenceFile[]
  annotations: Map<string, EvidenceAnnotation>
  currentIndex: number
  onIndexChange: (idx: number) => void
  onClose: () => void
  claimUuid: string
  taskUuid?: string | null
  readOnly: boolean
  userRole: 'rs' | 'lab'
  annotationsEnabled?: boolean
  /**
   * What the file at an index is, in the caller's terms. The carousel runs
   * across evidence categories, so the filename alone does not say which one
   * the reader is looking at.
   */
  captionFor?: (index: number) => string | undefined
}) {
  const { t } = useTranslation()
  const file = files[currentIndex]
  const ann = file ? annotations.get(file.uuid) : undefined
  const createAnnotation = useCreateEvidenceAnnotation(claimUuid)
  const updateAnnotation = useUpdateEvidenceAnnotation(
    claimUuid,
    ann?.uuid ?? ''
  )
  const createPin = useCreateEvidencePin(claimUuid, ann?.uuid ?? '')
  const imageContainerRef = useRef<HTMLDivElement>(null)
  // The full-resolution source is the file's stable, never-expiring proxy URL
  // (TA-23) - the capability token is carried in the URL, so the raw <img> the
  // pins are positioned over loads it directly with no fetch/blob round trip.
  const imageObjectUrl = file?.file_url

  const [isFlagged, setIsFlagged] = useState(ann?.is_flagged ?? false)
  const [flagCategory, setFlagCategory] = useState(ann?.flag_category ?? '')
  const [notes, setNotes] = useState(ann?.notes ?? '')
  // Full tenant document-category taxonomy (WP1-56 AC4) - the review
  // window's Category dropdown lists every category we collect, not a
  // hardcoded subset.
  const { data: flagCategories = [] } = useEvidenceFlagCategories()

  // Zoom (WP1-56 AC3). Scaling the image+pins container keeps pins aligned,
  // and getBoundingClientRect (used for pin placement) reflects the scaled
  // size, so click->% math stays correct. Reset when the image changes.
  const [zoom, setZoom] = useState(1)
  // Drag to move around the photo (WPS-223). A press that moves pans; a
  // press that does not is a click, so pin placement keeps working.
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [panning, setPanning] = useState(false)
  const dragRef = useRef<{
    x: number
    y: number
    panX: number
    panY: number
    moved: boolean
  } | null>(null)
  useEffect(() => {
    setZoom(1)
    setPan({ x: 0, y: 0 })
  }, [file?.uuid])
  const clampZoom = (z: number) =>
    Math.min(5, Math.max(0.25, Math.round(z * 100) / 100))

  const [activePin, setActivePin] = useState<string | null>(null)
  const [newPin, setNewPin] = useState<{ x: number; y: number } | null>(null)
  // Full-screen toggle (WP1-56) - the 95vw/95vh review window can expand to
  // fill the whole viewport for the largest possible working area.
  const [fullScreen, setFullScreen] = useState(false)

  const pins = ann?.pins ?? []

  const handleSave = () => {
    if (readOnly || !annotationsEnabled) return
    const payload = {
      evidence_file_uuid: file.uuid,
      is_flagged: isFlagged,
      flag_category: flagCategory,
      notes,
      ...(taskUuid ? { lab_task: taskUuid } : {}),
    }

    if (ann?.uuid) {
      updateAnnotation.mutate(payload)
    } else {
      createAnnotation.mutate(payload)
    }
  }

  const navigateTo = (idx: number) => {
    handleSave()
    setActivePin(null)
    setNewPin(null)
    const nextFile = files[idx]
    const nextAnn = nextFile ? annotations.get(nextFile.uuid) : undefined
    setIsFlagged(nextAnn?.is_flagged ?? false)
    setFlagCategory(nextAnn?.flag_category ?? '')
    setNotes(nextAnn?.notes ?? '')
    onIndexChange(idx)
  }

  const handleImageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (readOnly || !annotationsEnabled) return
    const rect = e.currentTarget.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * 100
    const y = ((e.clientY - rect.top) / rect.height) * 100

    if (!ann?.uuid) {
      const payload = {
        evidence_file_uuid: file.uuid,
        is_flagged: isFlagged,
        flag_category: flagCategory,
        notes,
        ...(taskUuid ? { lab_task: taskUuid } : {}),
      }
      createAnnotation.mutate(payload, {
        onSuccess: () => {
          setNewPin({ x, y })
        },
      })
    } else {
      setActivePin(null)
      setNewPin({ x, y })
    }
  }

  const onPanStart = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    dragRef.current = {
      x: e.clientX,
      y: e.clientY,
      panX: pan.x,
      panY: pan.y,
      moved: false,
    }
  }

  const onPanMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag) return
    const dx = e.clientX - drag.x
    const dy = e.clientY - drag.y
    if (!drag.moved && Math.abs(dx) + Math.abs(dy) < 4) return
    if (!drag.moved) {
      drag.moved = true
      setPanning(true)
      e.currentTarget.setPointerCapture(e.pointerId)
    }
    setPan({ x: drag.panX + dx, y: drag.panY + dy })
  }

  const onPanEnd = () => {
    if (dragRef.current && !dragRef.current.moved) dragRef.current = null
    setPanning(false)
  }

  const onImageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const dragged = dragRef.current?.moved
    dragRef.current = null
    if (dragged) return
    handleImageClick(e)
  }

  const handleSaveNewPin = (comment: string) => {
    if (!newPin || !ann?.uuid) return
    createPin.mutate(
      {
        x_pct: Math.round(newPin.x * 100) / 100,
        y_pct: Math.round(newPin.y * 100) / 100,
        comment,
        created_by_role: userRole,
      },
      {
        onSuccess: () => {
          setNewPin(null)
        },
      }
    )
  }

  if (!file) return null

  return (
    <Dialog open onOpenChange={() => { handleSave(); onClose() }}>
      <DialogContent
        className={cn(
          'transition-[width,height,max-width,max-height] duration-150',
          fullScreen
            ? '!max-w-[100vw] !max-h-[100vh] w-screen h-screen rounded-none'
            : '!max-w-[95vw] !max-h-[95vh] w-[95vw] h-[95vh]',
        )}
      >
        <DialogHeader>
          {/* Same toolbar as the plain file viewer: identity on the left,
              every control anchored right so nothing slides as the filename
              and category change width between photos. */}
          <DialogTitle className="flex items-center justify-between gap-3 pr-7">
            <span className="flex min-w-0 items-center gap-2">
              <span className="truncate text-[14px]">{file.file_name}</span>
              {captionFor?.(currentIndex) ? (
                <>
                  <span className="shrink-0 text-[var(--ink-3)]">·</span>
                  <span className="shrink-0 truncate text-[12px] font-normal text-[var(--ink-3)]">
                    {captionFor(currentIndex)}
                  </span>
                </>
              ) : null}
            </span>
            <span className="flex shrink-0 items-center gap-1">
              {files.length > 1 ? (
                <>
                  <span className="flex shrink-0 items-center gap-0.5">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      onClick={() => onIndexChange(currentIndex - 1)}
                      disabled={currentIndex <= 0}
                      aria-label={t('common.previous', 'Previous')}
                    >
                      <ChevronLeft className="h-3 w-3" />
                    </Button>
                    <span className="min-w-[42px] text-center text-[11px] font-normal tabular-nums text-[var(--ink-3)]">
                      {currentIndex + 1} / {files.length}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      onClick={() => onIndexChange(currentIndex + 1)}
                      disabled={currentIndex >= files.length - 1}
                      aria-label={t('common.next', 'Next')}
                    >
                      <ChevronRight className="h-3 w-3" />
                    </Button>
                  </span>
                  <span className="mx-0.5 h-4 w-px bg-[var(--rule)]" />
                </>
              ) : null}
              {file.file_url ? (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    size="xs"
                    onClick={() => window.open(file.file_url!, '_blank', 'noopener,noreferrer')}
                    aria-label={t('common.openInNewTab', 'Open in new tab')}
                    className="shrink-0 font-normal"
                  >
                    <ExternalLink className="h-3 w-3" />
                    {t('common.open', 'Open')}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="xs"
                    onClick={() => {
                      // The stable, never-expiring proxy URL with download=1
                      // flips Content-Disposition, so a plain anchor saves it.
                      const href = fileDownloadUrl(file.file_url)
                      if (!href) return
                      const a = document.createElement('a')
                      a.href = href
                      a.download = file.file_name || ''
                      document.body.appendChild(a)
                      a.click()
                      document.body.removeChild(a)
                    }}
                    aria-label={t('common.download', 'Download')}
                    className="shrink-0 font-normal"
                  >
                    <Download className="h-3 w-3" />
                    {t('common.download', 'Download')}
                  </Button>
                  <span className="mx-0.5 h-4 w-px bg-[var(--rule)]" />
                </>
              ) : null}
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => setFullScreen((v) => !v)}
                aria-label={
                  fullScreen
                    ? t('common.exitFullScreen', 'Exit full screen')
                    : t('common.enterFullScreen', 'Enter full screen')
                }
                className="shrink-0 cursor-pointer text-[var(--ink-3)] hover:text-[var(--ink)]"
              >
                {fullScreen ? (
                  <Minimize2 className="h-4 w-4" />
                ) : (
                  <Maximize2 className="h-4 w-4" />
                )}
              </Button>
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 gap-4">
          {/* Image with callout overlay */}
          <div
            className="relative flex flex-1 items-center justify-center overflow-auto rounded-[6px] bg-[var(--bg-light)]"
            onWheel={(e) => {
              e.preventDefault();
              setZoom((z) => clampZoom(z - e.deltaY * 0.0015));
            }}
          >
            <div
              ref={imageContainerRef}
              className={cn(
                'relative touch-none overflow-hidden rounded-[6px]',
                !panning && 'transition-transform duration-100',
                panning
                  ? 'cursor-grabbing'
                  : !readOnly && annotationsEnabled
                    ? 'cursor-crosshair'
                    : 'cursor-grab',
              )}
              onPointerDown={onPanStart}
              onPointerMove={onPanMove}
              onPointerUp={onPanEnd}
              onPointerCancel={onPanEnd}
              onClick={onImageClick}
              // Pan and zoom are runtime values -> inline style per the
              // dynamic-style exception. Moving this container moves the
              // pins with it.
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              }}
            >
              <img
                src={imageObjectUrl ?? undefined}
                alt={file.file_name}
                className={cn(
                  'w-full rounded-[6px] object-contain',
                  fullScreen ? 'max-h-[90vh]' : 'max-h-[82vh]',
                )}
                draggable={false}
              />

              {/* Existing pin callouts - always visible */}
              {pins.map((pin) => (
                <PinCallout
                  key={pin.uuid}
                  pin={pin}
                  isActive={activePin === pin.uuid}
                  claimUuid={claimUuid}
                  annotationUuid={ann?.uuid ?? ''}
                  containerRef={imageContainerRef}
                  readOnly={readOnly}
                  onClick={(e) => {
                    e.stopPropagation()
                    setNewPin(null)
                    setActivePin(activePin === pin.uuid ? null : pin.uuid)
                  }}
                />
              ))}

              {/* New pin being placed */}
              {newPin && (
                <NewPinInput
                  position={newPin}
                  onSave={handleSaveNewPin}
                  onCancel={() => setNewPin(null)}
                />
              )}

              {/* Active pin edit popover */}
              {activePin && (() => {
                const pin = pins.find((p) => p.uuid === activePin)
                if (!pin) return null
                return (
                  <ActivePinEditor
                    key={pin.uuid}
                    pin={pin}
                    claimUuid={claimUuid}
                    annotationUuid={ann?.uuid ?? ''}
                    readOnly={readOnly}
                    onClose={() => setActivePin(null)}
                  />
                )
              })()}
            </div>

            {/* Nav arrows */}
            {currentIndex > 0 && (
              <button
                type="button"
                onClick={() => navigateTo(currentIndex - 1)}
                className="absolute left-2 top-1/2 flex h-8 w-8 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/50 text-white transition hover:bg-black/70"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            )}
            {currentIndex < files.length - 1 && (
              <button
                type="button"
                onClick={() => navigateTo(currentIndex + 1)}
                className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/50 text-white transition hover:bg-black/70"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            )}

            {/* Zoom controls (WP1-56) */}
            <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-lg border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-1 py-1 shadow-[var(--shadow-float)]">
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => setZoom((z) => clampZoom(z - 0.25))}
                disabled={zoom <= 0.25}
                aria-label="Zoom out"
              >
                <Minus className="h-4 w-4" />
              </Button>
              <button
                type="button"
                onClick={() => {
                  setZoom(1)
                  setPan({ x: 0, y: 0 })
                }}
                className="flex min-w-[44px] items-center justify-center gap-1 px-1 text-[11px] tabular-nums text-[var(--ink-2)] hover:text-[var(--ink)]"
                aria-label="Reset zoom"
              >
                {zoom !== 1 ? <RotateCcw className="h-3 w-3" /> : null}
                {Math.round(zoom * 100)}%
              </button>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => setZoom((z) => clampZoom(z + 0.25))}
                disabled={zoom >= 5}
                aria-label="Zoom in"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Annotation sidebar */}
          {annotationsEnabled && (
            <div
              className={cn(
                'flex w-[280px] shrink-0 flex-col gap-3 overflow-y-auto',
                fullScreen ? 'max-h-[92vh]' : 'max-h-[85vh]',
              )}
            >
              {!readOnly && (
                <div className="text-[12px] leading-snug text-[var(--ink-3)]">
                  <p className="font-semibold text-[var(--green)]">
                    {t('claims.lightbox.instructionsTitle', 'Add Pin, Flag or Notes')}
                  </p>
                  <ul className="mt-1.5 flex flex-col gap-1">
                    {[
                      [
                        t('claims.lightbox.pinAction', 'Pin'),
                        t('claims.lightbox.pinHint', 'click anywhere on the photo'),
                      ],
                      [
                        t('claims.lightbox.flagAction', 'Flag'),
                        t('claims.lightbox.flagHint', 'mark the whole photo'),
                      ],
                      [
                        t('claims.lightbox.notesAction', 'Notes'),
                        t('claims.lightbox.notesHint', 'add any extra detail'),
                      ],
                    ].map(([action, hint]) => (
                      <li key={action} className="flex gap-1.5">
                        <span className="text-[var(--ink-4)]">&middot;</span>
                        <span>
                          <span className="font-medium text-[var(--ink-2)]">{action}</span>
                          {' - '}
                          {hint}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Flag toggle */}
              <Button
                variant="outline"
                size="xs"
                onClick={() => setIsFlagged(!isFlagged)}
                disabled={readOnly}
                className={cn(
                  'mt-2 w-fit self-start',
                  isFlagged &&
                    'border-[#e67e22] text-[#e67e22] hover:bg-[#e67e22]/5'
                )}
              >
                <Flag className="mr-1 h-3 w-3" />
                {isFlagged ? 'Flagged' : 'Flag'}
              </Button>

              {/* Category */}
              {isFlagged && (
                <div>
                  <p className="mb-1 text-[10px] font-medium uppercase tracking-[0.08em] text-[var(--ink-3)]">
                    Category
                  </p>
                  <Select
                    value={flagCategory}
                    onValueChange={setFlagCategory}
                    disabled={readOnly}
                  >
                    <SelectTrigger className="h-8 text-[12px]">
                      <SelectValue placeholder="Select category…" />
                    </SelectTrigger>
                    <SelectContent>
                      {flagCategories.map((cat) => (
                        <SelectItem key={cat.key} value={cat.key}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Notes */}
              <div>
                <p className="mb-1 text-[10px] font-medium uppercase tracking-[0.08em] text-[var(--ink-3)]">
                  Notes
                </p>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={readOnly}
                  placeholder="Describe what you observe…"
                  rows={4}
                  className="w-full resize-none rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-2.5 py-2 text-[12px] text-[var(--ink)] placeholder:text-[var(--ink-3)] focus:border-[var(--accent)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                />
              </div>

              <Button
                size="sm"
                onClick={handleSave}
                disabled={readOnly || createAnnotation.isPending || updateAnnotation.isPending}
                className="w-fit self-start"
              >
                {createAnnotation.isPending || updateAnnotation.isPending
                  ? 'Saving…'
                  : 'Save Annotation'}
              </Button>

              {/* Pin annotations list */}
              {pins.length > 0 && (
                <div className="border-t-[0.5px] border-[var(--rule)] pt-3">
                  <p className="mb-2 text-[10px] font-medium uppercase tracking-[0.08em] text-[var(--ink-3)]">
                    Annotations ({pins.length})
                  </p>
                  <div className="flex flex-col gap-1.5">
                    {pins.map((pin) => {
                      const isRS = pin.created_by_role === 'rs'
                      return (
                        <button
                          key={pin.uuid}
                          type="button"
                          onClick={() => {
                            setNewPin(null)
                            setActivePin(activePin === pin.uuid ? null : pin.uuid)
                          }}
                          className={cn(
                            'flex items-start gap-2 rounded-[4px] px-2 py-1.5 text-left transition-colors',
                            activePin === pin.uuid
                              ? 'bg-[var(--accent)]/10'
                              : 'hover:bg-[var(--bg-subtle)]'
                          )}
                        >
                          <span
                            className={cn(
                              'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[9px] font-bold text-white',
                              pinPalette(pin.pin_number).dot
                            )}
                          >
                            {pin.pin_number}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-[11px] font-medium text-[var(--ink)]">
                              {pin.comment || 'No label'}
                            </p>
                            <p className="text-[9px] text-[var(--ink-3)]">
                              {pin.created_by_name ?? 'Unknown'}
                              <Badge
                                variant="outline"
                                className="ml-1 px-1 py-0 text-[8px]"
                              >
                                {isRS ? 'RS' : 'LAB'}
                              </Badge>
                            </p>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ── Active pin editor wrapper ───────────────────────────────────────

function ActivePinEditor({
  pin,
  claimUuid,
  annotationUuid,
  readOnly,
  onClose,
}: {
  pin: EvidencePin
  claimUuid: string
  annotationUuid: string
  readOnly: boolean
  onClose: () => void
}) {
  const updatePin = useUpdateEvidencePin(claimUuid, annotationUuid, pin.uuid)
  const deletePin = useDeleteEvidencePin(claimUuid, annotationUuid, pin.uuid)

  return (
    <PinEditPopover
      pin={pin}
      onSave={(comment) => {
        updatePin.mutate(
          { x_pct: Number(pin.x_pct), y_pct: Number(pin.y_pct), comment },
          { onSuccess: onClose },
        )
      }}
      onDelete={() => {
        deletePin.mutate(undefined, { onSuccess: onClose })
      }}
      onClose={onClose}
      readOnly={readOnly}
    />
  )
}
